// features/finances/components/CopiarPeriodoDialog.tsx
// ============================================================================
// «Copiar un periodo» del presupuesto (B2 ampliado, 25-oct-2026). Mabel (23-oct 15:18) viene «con los presupuestos del
// siguiente semestre»: en vez de copiar mes por mes, elige cuántos meses (3, 6, 12 o los que sean), desde qué mes y a qué
// mes empieza el destino. Sirve para gastos e ingresos (POST /finances/presupuesto/copiar-rango). El destino queda
// exactamente como el origen, mes a mes («solo ese mes»); lo de antes y después del destino no cambia.
// ============================================================================
import { useState, type ReactNode } from 'react';
import { ArrowRight, X } from 'lucide-react';
import { copiarRangoPresupuesto, CopiaRango } from '../presupuestoIngresosApi';
import { useToast } from '../../../context/ToastContext';

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
export function sumaMeses(ym: string, n: number) { const [y, m] = ym.split('-').map(Number); const d = new Date(y, m - 1 + n, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; }
export const mesLargo = (ym: string) => { const [y, m] = ym.split('-').map(Number); return `${MESES[m - 1]} ${y}`; };
export const mesCorto = (ym: string) => { const [y, m] = ym.split('-').map(Number); return `${MESES[m - 1].slice(0, 3)} ${String(y).slice(2)}`; };
const input = 'w-full rounded-lg border border-line bg-surface px-3 py-2 text-content outline-none focus:border-brand-blue';
const btn = 'rounded-lg px-4 py-2 text-sm font-medium transition disabled:opacity-50';
const esMes = (s: string) => /^\d{4}-\d{2}$/.test(s);

export function Modal({ titulo, onClose, children }: { titulo: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative flex max-h-[92vh] w-full max-w-md flex-col rounded-t-2xl border border-line bg-surface shadow-xl sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <h3 className="font-display text-lg font-bold text-content">{titulo}</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>
        <div className="space-y-3 overflow-y-auto px-5 py-4 text-sm">{children}</div>
      </div>
    </div>
  );
}

export default function CopiarPeriodoDialog({ tipo, desdeInicial, onClose, onCopiado }:
  { tipo: 'gastos' | 'ingresos'; desdeInicial: string; onClose: () => void; onCopiado: (r: CopiaRango) => void }) {
  const toast = useToast();
  const [desde, setDesde] = useState(desdeInicial);
  const [n, setN] = useState(6);
  const [a, setA] = useState(() => sumaMeses(desdeInicial, 6));
  const [guardando, setGuardando] = useState(false);
  const valido = esMes(desde) && esMes(a) && n >= 1 && n <= 12;
  const finOrigen = esMes(desde) ? sumaMeses(desde, n - 1) : '';
  const finDestino = esMes(a) ? sumaMeses(a, n - 1) : '';
  const traslape = valido && !(a > finOrigen || finDestino < desde);
  const nombre = tipo === 'gastos' ? 'presupuesto de gastos' : 'objetivo de ingresos';

  function elegirN(v: number) { setN(v); if (esMes(desde)) setA(sumaMeses(desde, v)); }
  async function copiar() {
    if (!valido || traslape) return;
    setGuardando(true);
    try {
      const r = await copiarRangoPresupuesto(tipo, { desde, n, a });
      toast.success(r.cambiados ? `Listo: ${mesCorto(a)} – ${mesCorto(finDestino)} quedó como ${mesCorto(desde)} – ${mesCorto(finOrigen)} (${r.cambiados} celda${r.cambiados === 1 ? '' : 's'})`
        : `${mesCorto(a)} – ${mesCorto(finDestino)} ya era igual que ${mesCorto(desde)} – ${mesCorto(finOrigen)}`);
      onCopiado(r);
    } catch (e: any) { toast.error(e?.message || 'No se pudo copiar'); } finally { setGuardando(false); }
  }
  return (
    <Modal titulo={`Copiar un periodo · ${tipo === 'gastos' ? 'gastos' : 'ingresos'}`} onClose={onClose}>
      <p className="text-content-3">Copia el {nombre} de varios meses seguidos (por ejemplo, un semestre) a otro periodo, mes a mes. Lo de antes y después del destino no cambia.</p>
      <fieldset>
        <span className="mb-1 block text-xs font-medium text-content-2">Cuántos meses</span>
        <div className="flex flex-wrap gap-1.5">
          {[3, 6, 12].map((v) => (
            <button key={v} type="button" onClick={() => elegirN(v)}
              className={`rounded-lg border px-3 py-1.5 text-sm transition ${n === v ? 'border-accent bg-accent text-on-accent' : 'border-line bg-surface text-content-2 hover:bg-surface-2'}`}>
              {v === 3 ? 'Trimestre (3)' : v === 6 ? 'Semestre (6)' : 'Año (12)'}
            </button>
          ))}
          <input type="number" min={1} max={12} value={n} onChange={(ev) => elegirN(Math.min(12, Math.max(1, Number(ev.target.value) || 1)))}
            className="w-20 rounded-lg border border-line bg-surface px-2 py-1.5 text-sm text-content outline-none focus:border-brand-blue" aria-label="Número de meses" />
        </div>
      </fieldset>
      <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-content-2">Copiar desde</span>
          <input type="month" value={desde} onChange={(ev) => { setDesde(ev.target.value); if (esMes(ev.target.value)) setA(sumaMeses(ev.target.value, n)); }} className={input} />
        </label>
        <ArrowRight size={18} className="mb-2.5 text-content-3" />
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-content-2">Pegar a partir de</span>
          <input type="month" value={a} onChange={(ev) => setA(ev.target.value)} className={input} />
        </label>
      </div>
      {valido && (
        <p className={`rounded-lg px-3 py-2 text-xs ${traslape ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400' : 'bg-surface-2 text-content-2'}`} data-testid="copiar-periodo-resumen">
          {traslape ? 'El destino se encima con el origen: elige otro mes de inicio.'
            : <><b className="capitalize">{mesLargo(desde)}</b> – <b className="capitalize">{mesLargo(finOrigen)}</b> → <b className="capitalize">{mesLargo(a)}</b> – <b className="capitalize">{mesLargo(finDestino)}</b></>}
        </p>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onClose} className={`${btn} text-content-2 hover:bg-surface-2`}>Cancelar</button>
        <button onClick={copiar} disabled={guardando || !valido || traslape} className={`${btn} bg-accent text-on-accent`}>Copiar</button>
      </div>
    </Modal>
  );
}
